/**
 * Binding Conflict Detector
 *
 * Detects setting conflicts that arise when:
 *   1. Binding a standalone Track to a Cycle (detectTrackCycleConflicts)
 *   2. Linking a Session to a Track (detectSessionTrackConflicts)
 *
 * A "conflict" means the incoming layer has a setting that differs from the
 * entity being attached. In both cases the cascade (cycle > track > session)
 * means the higher layer will win — the conflict is informational so the user
 * can give informed consent before proceeding.
 *
 * Severity:
 *   'override' — the incoming setting will take precedence; the lower layer's
 *                value remains stored but is superseded at runtime.
 *   'lock'     — the incoming layer has allowTrackOverride=false or
 *                allowSessionOverride=false; the lower layer cannot deviate
 *                from the higher layer's settings at all.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export interface BindingConflict {
  /** Machine key identifying the conflicting field */
  field: string;
  /** Human-readable field label */
  label: string;
  /** The current entity's value (track value when binding to cycle; session value when linking to track) */
  currentValue: string;
  /** The incoming entity's value (cycle's value; track's required value) */
  incomingValue: string;
  /** One-line description of the impact */
  description: string;
  /** Whether this is an outright lock (no override permitted) or a precedence override */
  severity: 'override' | 'lock';
}

export type ConflictResolution = 'accept' | 'cancel';

// ── Label helpers ────────────────────────────────────────────────────────────

function timingLabel(v: string | null | undefined): string {
  if (v === 'live') return 'Live (synchronous)';
  if (v === 'async') return 'Async';
  return v ?? '—';
}

function entryLabel(v: string | null | undefined): string {
  if (v === 'facilitator') return 'Facilitator-controlled';
  if (v === 'participants') return 'Participants-controlled';
  return v ?? '—';
}

function resolLabel(v: string | null | undefined): string {
  if (v === 'majority') return 'Majority';
  if (v === 'consensus') return 'Consensus';
  if (v === 'facilitator_call') return 'Facilitator call';
  if (v === 'average') return 'Average score';
  return v ?? '—';
}

function facilitatorLabel(v: string | null | undefined): string {
  if (!v) return '—';
  return `User ${v.slice(0, 8)}…`;
}

// ── Helper: read session defaults from cycle metadata or first-class columns ──

interface CycleDefaults {
  timingMode?: string | null;
  entryMode?: string | null;
  defaultFacilitatorId?: string | null;
  resultResolution?: string | null;
  allowTrackOverride?: boolean;
}

function cycleDefaults(cycle: {
  metadata?: Record<string, unknown> | null;
  defaultTimingMode?: string | null;
  defaultEntryMode?: string | null;
  defaultFacilitatorId?: string | null;
}): CycleDefaults {
  const meta = cycle.metadata?.sessionDefaults as Record<string, unknown> | undefined;
  return {
    timingMode: (cycle.defaultTimingMode ?? meta?.timingMode ?? null) as string | null,
    entryMode: (cycle.defaultEntryMode ?? meta?.entryMode ?? null) as string | null,
    defaultFacilitatorId: (cycle.defaultFacilitatorId ?? meta?.defaultFacilitatorId ?? null) as string | null,
    resultResolution: (meta?.resultResolution ?? null) as string | null,
    // The cycle screen saves the switch at metadata.allowTrackOverride
    // (CycleSessionDefaultsSection); this read looked only under sessionDefaults,
    // so the lock it guards was never seen (S-40). Read both, the saved place first.
    allowTrackOverride: (cycle.metadata?.allowTrackOverride ?? meta?.allowTrackOverride) !== false, // default: true (track may override)
  };
}

interface TrackDefaults {
  timingMode?: string | null;
  entryMode?: string | null;
  defaultFacilitatorId?: string | null;
  resultResolutionOverride?: string | null;
  allowSessionOverride?: boolean;
}

function trackDefaults(track: {
  metadata?: Record<string, unknown> | null;
  defaultTimingMode?: string | null;
  defaultEntryMode?: string | null;
  defaultFacilitatorId?: string | null;
  resultResolutionOverride?: string | null;
}): TrackDefaults {
  const meta = track.metadata?.sessionDefaults as Record<string, unknown> | undefined;
  return {
    timingMode: (track.defaultTimingMode ?? meta?.timingMode ?? null) as string | null,
    entryMode: (track.defaultEntryMode ?? meta?.entryMode ?? null) as string | null,
    defaultFacilitatorId: (track.defaultFacilitatorId ?? meta?.defaultFacilitatorId ?? null) as string | null,
    resultResolutionOverride: (track.resultResolutionOverride ?? null) as string | null,
    allowSessionOverride:
      typeof track.metadata?.allowSessionOverride === 'boolean'
        ? (track.metadata.allowSessionOverride as boolean)
        : true, // default: true (sessions may override)
  };
}

// ── 1. Track → Cycle conflicts ───────────────────────────────────────────────

/**
 * Detect conflicts that arise when binding a standalone Track to a Cycle.
 *
 * Returns the list of settings where the cycle's value will override or lock
 * out the track's current configuration.
 */
export function detectTrackCycleConflicts(
  track: Parameters<typeof trackDefaults>[0],
  cycle: Parameters<typeof cycleDefaults>[0]
): BindingConflict[] {
  const cd = cycleDefaults(cycle);
  const td = trackDefaults(track);
  const conflicts: BindingConflict[] = [];

  // If the cycle locks track overrides entirely, that's a single overarching conflict
  if (!cd.allowTrackOverride) {
    conflicts.push({
      field: 'allowTrackOverride',
      label: 'Track overrides disabled',
      currentValue: 'Track has custom session defaults',
      incomingValue: 'Cycle enforces: tracks may not override',
      description: 'The cycle does not allow tracks to set their own session defaults. All of the track\'s session configuration will be ignored — the cycle\'s settings apply to all sessions.',
      severity: 'lock',
    });
    // Still list individual field conflicts so the user can see what specifically changes
  }

  if (cd.timingMode && td.timingMode && cd.timingMode !== td.timingMode) {
    conflicts.push({
      field: 'timingMode',
      label: 'Session timing',
      currentValue: timingLabel(td.timingMode),
      incomingValue: timingLabel(cd.timingMode),
      description: `The cycle enforces ${timingLabel(cd.timingMode)} mode. The track's ${timingLabel(td.timingMode)} default will be overridden.`,
      severity: 'override',
    });
  }

  if (cd.entryMode && td.entryMode && cd.entryMode !== td.entryMode) {
    conflicts.push({
      field: 'entryMode',
      label: 'Response entry mode',
      currentValue: entryLabel(td.entryMode),
      incomingValue: entryLabel(cd.entryMode),
      description: `The cycle enforces ${entryLabel(cd.entryMode)}. The track's ${entryLabel(td.entryMode)} default will be overridden.`,
      severity: 'override',
    });
  }

  if (
    cd.defaultFacilitatorId &&
    td.defaultFacilitatorId &&
    cd.defaultFacilitatorId !== td.defaultFacilitatorId
  ) {
    conflicts.push({
      field: 'facilitator',
      label: 'Default facilitator',
      currentValue: facilitatorLabel(td.defaultFacilitatorId),
      incomingValue: facilitatorLabel(cd.defaultFacilitatorId),
      description: 'The cycle has a different default facilitator. The cycle\'s facilitator will take precedence for new sessions.',
      severity: 'override',
    });
  }

  if (
    cd.resultResolution &&
    td.resultResolutionOverride &&
    cd.resultResolution !== td.resultResolutionOverride
  ) {
    conflicts.push({
      field: 'resultResolution',
      label: 'Result resolution',
      currentValue: resolLabel(td.resultResolutionOverride),
      incomingValue: resolLabel(cd.resultResolution),
      description: `The cycle uses ${resolLabel(cd.resultResolution)} resolution. The track's ${resolLabel(td.resultResolutionOverride)} override will be superseded.`,
      severity: 'override',
    });
  }

  return conflicts;
}

// ── 2. Session → Track conflicts ─────────────────────────────────────────────

interface SessionSnapshot {
  modeConfig?: {
    timingMode?: string | null;
    entryMode?: string | null;
  } | null;
  facilitatorId?: string | null;
  criteriaConfig?: { criteriaIds?: string[] } | null;
  participantIds?: string[];
}

/**
 * Detect conflicts that arise when linking an existing Session to a Track.
 *
 * Returns the list of settings where the track's requirements differ from
 * the session's current configuration.
 */
export function detectSessionTrackConflicts(
  session: SessionSnapshot,
  track: Parameters<typeof trackDefaults>[0] & {
    participationLockedAt?: Date | string | null;
    criteriaLockedAt?: Date | string | null;
  },
  options?: {
    /** IDs of the track's effective participants (for structural check) */
    trackParticipantIds?: string[];
    /** IDs of the track's locked criteria (for structural check) */
    trackCriteriaIds?: string[];
  }
): BindingConflict[] {
  const td = trackDefaults(track);
  const conflicts: BindingConflict[] = [];

  // Overarching lock: track disallows session overrides
  if (!td.allowSessionOverride) {
    conflicts.push({
      field: 'allowSessionOverride',
      label: 'Session overrides disabled',
      currentValue: 'Session has custom settings',
      incomingValue: 'Track enforces: sessions may not override',
      description: 'This track does not allow sessions to deviate from its defaults. All session-level config will be replaced by the track\'s requirements.',
      severity: 'lock',
    });
  }

  // Timing mode
  const sessionTiming = session.modeConfig?.timingMode;
  if (td.timingMode && sessionTiming && td.timingMode !== sessionTiming) {
    conflicts.push({
      field: 'timingMode',
      label: 'Session timing',
      currentValue: timingLabel(sessionTiming),
      incomingValue: timingLabel(td.timingMode),
      description: `This session is configured as ${timingLabel(sessionTiming)}, but the track requires ${timingLabel(td.timingMode)}.`,
      severity: 'override',
    });
  }

  // Entry mode
  const sessionEntry = session.modeConfig?.entryMode;
  if (td.entryMode && sessionEntry && td.entryMode !== sessionEntry) {
    conflicts.push({
      field: 'entryMode',
      label: 'Response entry mode',
      currentValue: entryLabel(sessionEntry),
      incomingValue: entryLabel(td.entryMode),
      description: `This session uses ${entryLabel(sessionEntry)}, but the track requires ${entryLabel(td.entryMode)}.`,
      severity: 'override',
    });
  }

  // Facilitator
  const sessionFacilitator = session.facilitatorId;
  if (
    td.defaultFacilitatorId &&
    sessionFacilitator &&
    td.defaultFacilitatorId !== sessionFacilitator
  ) {
    conflicts.push({
      field: 'facilitator',
      label: 'Facilitator',
      currentValue: facilitatorLabel(sessionFacilitator),
      incomingValue: facilitatorLabel(td.defaultFacilitatorId),
      description: 'The track has a different default facilitator. The track\'s facilitator will apply to sessions run under it.',
      severity: 'override',
    });
  }

  // Structural: participants outside the track's locked participant set
  if (
    track.participationLockedAt &&
    options?.trackParticipantIds &&
    session.participantIds &&
    session.participantIds.length > 0
  ) {
    const trackSet = new Set(options.trackParticipantIds);
    const outsideTrack = session.participantIds.filter((id) => !trackSet.has(id));
    if (outsideTrack.length > 0) {
      conflicts.push({
        field: 'participants',
        label: 'Participants outside track',
        currentValue: `${session.participantIds.length} session participant${session.participantIds.length !== 1 ? 's' : ''}`,
        incomingValue: `Track has ${options.trackParticipantIds.length} locked participant${options.trackParticipantIds.length !== 1 ? 's' : ''}`,
        description: `${outsideTrack.length} session participant${outsideTrack.length !== 1 ? 's are' : ' is'} not in the track's locked participant list. Only track participants will be effective.`,
        severity: 'override',
      });
    }
  }

  // Structural: session criteria not in track's locked criteria
  if (
    track.criteriaLockedAt &&
    options?.trackCriteriaIds &&
    session.criteriaConfig?.criteriaIds &&
    session.criteriaConfig.criteriaIds.length > 0
  ) {
    const trackCriteriaSet = new Set(options.trackCriteriaIds);
    const outsideCriteria = session.criteriaConfig.criteriaIds.filter(
      (id) => !trackCriteriaSet.has(id)
    );
    if (outsideCriteria.length > 0) {
      conflicts.push({
        field: 'criteria',
        label: 'Criteria outside track',
        currentValue: `${session.criteriaConfig.criteriaIds.length} session criteria`,
        incomingValue: `Track has ${options.trackCriteriaIds.length} locked criteria`,
        description: `${outsideCriteria.length} session criteria item${outsideCriteria.length !== 1 ? 's are' : ' is'} not in the track's locked criteria set. Only the track's accepted criteria will be effective.`,
        severity: 'override',
      });
    }
  }

  return conflicts;
}
